import { useEffect, useMemo } from 'react'
import { useApiInfinite } from '../../utils/useApi'
import { ClientFilters, buildFlatUnits, buildGroupUnits, distinctPlateCount } from './detectionFilters'
import {
    DetectionResult,
    GetDetectionsRequest,
    GetDetectionsResponse,
    GetDetectionsResponseSchema
} from './get_detections'
import { DETECTION_EXPLORER_PAGE_SIZE } from './config'

// Server page sizes used while accumulating. Flat mode mirrors the export (the backend honours a
// large page_size when group_by_license_plate=false); grouped requests are capped at 100 server-side.
const FLAT_SERVER_PAGE_SIZE = 2000
const GROUPED_SERVER_PAGE_SIZE = 100

type Args = {
    request: GetDetectionsRequest
    clientFilters: ClientFilters
    isGroupByLP: boolean
    /** 1-based display page index (reused from filters.page). */
    page: number
    enabled: boolean
}

export type FilteredDetections = {
    /** Detections for the current display page (flattened from the page's units). */
    items: DetectionResult[]
    /** How many units (detections, or plates when grouped) have been scanned so far. */
    scannedCount: number
    /** Total units the server reports (detections, or plates when grouped). */
    serverTotal: number
    /** Whether a Next page is available (more accumulated units or more server pages). */
    hasMore: boolean
    /** Fetching additional server pages to fill the current display page. */
    isLoadingMore: boolean
    /** First page still loading (nothing to show yet). */
    isInitialLoading: boolean
    isError: boolean
    error: Error | null
}

/**
 * Client-side result filtering with page-filling pagination. The backend can't filter by image
 * presence or "error event", so we page through it (large server pages), accumulate, apply the
 * predicates, and slice DETECTION_EXPLORER_PAGE_SIZE units per display page — fetching more server
 * pages until the current display page is full or the data is exhausted.
 */
export const useFilteredDetections = ({
    request,
    clientFilters,
    isGroupByLP,
    page,
    enabled
}: Args): FilteredDetections => {
    const serverPageSize = isGroupByLP ? GROUPED_SERVER_PAGE_SIZE : FLAT_SERVER_PAGE_SIZE

    const query = useApiInfinite<typeof GetDetectionsResponseSchema, number>(
        '/platform/get_detections',
        // `page` comes from pageParam and `page_size` is fixed here, so the request's own page index
        // (the display page) is overridden per server page.
        (pageParam) => ({ ...request, page: pageParam ?? 1, page_size: serverPageSize }),
        GetDetectionsResponseSchema,
        {
            // Exclude the display page from the key (page: undefined is dropped when hashed) so the
            // whole accumulated set keeps one cache entry as the user pages through it.
            queryKey: ['detectionExplorerFiltered', { ...request, page: undefined, page_size: serverPageSize }],
            enabled,
            retry: false,
            keepPreviousData: true,
            getNextPageParam: (lastPage: GetDetectionsResponse, allPages: GetDetectionsResponse[]) => {
                const totalPages = Math.ceil((lastPage.total ?? 0) / serverPageSize)
                return allPages.length < totalPages ? allPages.length + 1 : undefined
            }
        }
    )

    const pages = useMemo(() => query.data?.pages ?? [], [query.data])
    const accumulated = useMemo(() => pages.flatMap((p) => p.results), [pages])
    const serverTotal = pages[0]?.total ?? 0

    const units = useMemo(
        () => (isGroupByLP ? buildGroupUnits(accumulated, clientFilters) : buildFlatUnits(accumulated, clientFilters)),
        [accumulated, clientFilters, isGroupByLP]
    )

    // In grouped mode the count unit is plates (matching serverTotal); in flat mode it's detections.
    const scannedCount = useMemo(
        () => (isGroupByLP ? distinctPlateCount(accumulated) : accumulated.length),
        [accumulated, isGroupByLP]
    )

    const start = (page - 1) * DETECTION_EXPLORER_PAGE_SIZE
    const pageUnits = units.slice(start, start + DETECTION_EXPLORER_PAGE_SIZE)
    const items = pageUnits.flat()

    // Keep pulling server pages until the current display page is full (or the data runs out).
    const needed = page * DETECTION_EXPLORER_PAGE_SIZE
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = query
    useEffect(() => {
        if (!enabled) return
        if (units.length < needed && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [enabled, units.length, needed, hasNextPage, isFetchingNextPage, fetchNextPage])

    return {
        items,
        scannedCount,
        serverTotal,
        hasMore: units.length > start + DETECTION_EXPLORER_PAGE_SIZE || Boolean(hasNextPage),
        isLoadingMore: isFetchingNextPage,
        isInitialLoading: enabled && query.isLoading,
        isError: query.isError,
        error: query.error
    }
}
